import { Descriptions, Space, Tag, Tooltip } from "antd";
import {
  ChangePercent,
  SampleSizeWithWarning,
} from "./metric-performance/component/metric-performance-item/report-data-v2/variant-report";
import { MIN_VARIANT_SAMPLE_SIZE } from "@src/constant/ab-testing";
import { ABExplorationKeyWords } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import React from "react";

type ReportType = {
  method: string;
  p_value: number;
  mean: number;
  uplift_percent: number;
  lower_bound_uplift_percent: number;
  upper_bound_uplift_percent: number;
  error_message?: string;
  user_count: number;
};

export const getColorSignificant = ({
  significanceLevel,
  pValue,
  upliftPercent,
}: {
  significanceLevel: number;
  pValue?: number;
  upliftPercent?: number;
}) => {
  const color = {
    bg: "bg-neutral-100",
    upliftPercentTextColor: "text-slate-900",
  };
  if (!pValue || !upliftPercent) {
    return color;
  }

  if (pValue < significanceLevel) {
    color.bg = upliftPercent > 0 ? "bg-green-100" : "bg-red-100";
    color.upliftPercentTextColor = upliftPercent > 0 ? "text-green-500" : "text-red-500";
  }
  return color;
};

export const VariantReportToolTip: React.FC<{
  groupAlias: string;
  report: ReportType;
  children: React.ReactNode;
  significanceLevel: number;
}> = ({ groupAlias, report, significanceLevel, children }) => {
  const {
    method,
    p_value,
    mean: mean_variant,
    uplift_percent,
    lower_bound_uplift_percent,
    upper_bound_uplift_percent,
    error_message,
    [ABExplorationKeyWords.SAMPLE_SIZE]: sampleSize,
  } = report || {};

  const isSignificant = p_value !== null && p_value !== undefined ? p_value < significanceLevel : false;

  return (
    <Tooltip
      mouseEnterDelay={0.2}
      destroyOnHidden
      title={
        <Descriptions
          title={groupAlias}
          size="small"
          column={{ xs: 2, sm: 2, md: 2, lg: 5, xl: 5, xxl: 5 }}
          extra={isSignificant ? <Tag className="text-[20px] font-light">Significant</Tag> : null}
        >
          <Descriptions.Item label="Method" span={5}>
            {method ? method : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Sample size" span={5}>
            <SampleSizeWithWarning
              sampleSize={sampleSize as number}
              shouldWarning={(sampleSize) => sampleSize === undefined || sampleSize < MIN_VARIANT_SAMPLE_SIZE}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Mean" span={3}>
            {mean_variant !== undefined ? mean_variant : "-"}
          </Descriptions.Item>
          <Descriptions.Item label="% Change" span={2}>
            <ChangePercent
              changePercent={uplift_percent}
              textColorClassName={
                getColorSignificant({
                  significanceLevel: significanceLevel,
                  pValue: p_value,
                  upliftPercent: uplift_percent,
                }).upliftPercentTextColor
              }
            />
          </Descriptions.Item>
          <Descriptions.Item label="Credible Interval" span={5}>
            <Space>
              {"["}
              {lower_bound_uplift_percent ? `${((lower_bound_uplift_percent * 100) as number).toFixed(3)} %` : "-"}
              {","}
              {upper_bound_uplift_percent ? `${((upper_bound_uplift_percent * 100) as number).toFixed(3)} %` : "-"}
              {"]"}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label="P-Value" span={2}>
            {p_value ? ((p_value as number) > 0.001 ? (p_value as number).toFixed(3) : "< 0.001") : "-"}
          </Descriptions.Item>
          {error_message !== undefined && error_message !== null ? (
            <Descriptions.Item label="Error" span={3}>
              <div className="text-red-400">{error_message}</div>
            </Descriptions.Item>
          ) : null}
        </Descriptions>
      }
      style={{ maxWidth: "900px" }}
      overlayInnerStyle={{ width: "500px", padding: "20px", backgroundColor: "#fff" }}
      overlayStyle={{ width: "500px", maxWidth: "500px" }}
      placement="top"
      arrow={false}
    >
      {children}
    </Tooltip>
  );
};
